import dayjs from "dayjs"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"
import { useMemo } from "react"

import useUser from "@hooks/useUser"

import { UseScheduleHorizonProps } from "@organisms/ObjectsView/ObjectsView.types"

import { SCHEDULE_HORIZON_OPTIONS, ScheduleHorizon } from "@constants/scheduleHorizon"

dayjs.extend(timezone)
dayjs.extend(utc)

const DEFAULT_SCHEDULE_HORIZON: ScheduleHorizon = "all_dates"
const SCHEDULE_HORIZON_FILTER_PARAM = "scheduled_before"

// The window opens at the start of today in the company's timezone and covers the preset's fixed number of
// days, so it rolls forward daily. The bound is exclusive — midnight after the last day — which keeps the
// whole final day inside the window. `null` when no bound applies, or before the timezone is known.
function getScheduleHorizonUpperBound(horizon: ScheduleHorizon, preferredTimezone: string | undefined) {
    const selectedOption = SCHEDULE_HORIZON_OPTIONS.find((option) => option.value === horizon)

    if (!preferredTimezone || !selectedOption?.days) {
        return null
    }

    return dayjs().tz(preferredTimezone).startOf("day").add(selectedOption.days, "day").toISOString()
}

export default function useScheduleHorizon(props: UseScheduleHorizonProps) {
    const { objectType, activePhase, savedScheduleHorizon, saveScheduleHorizon, filters } = props

    const { user } = useUser()

    // `useUser` seeds its atom in an effect, so the first render has no user; nothing is bound until it does.
    const preferredTimezone = user?.service_company?.preferred_timezone

    // The horizon only makes sense where jobs are still being dispatched.
    const isScheduleHorizonAvailable = objectType === "job" && activePhase === "open"

    const scheduleHorizon = savedScheduleHorizon ?? DEFAULT_SCHEDULE_HORIZON

    const scheduleHorizonUpperBound = useMemo(() => {
        if (isScheduleHorizonAvailable) {
            return getScheduleHorizonUpperBound(scheduleHorizon, preferredTimezone)
        }

        return null
    }, [isScheduleHorizonAvailable, scheduleHorizon, preferredTimezone])

    // Ride the bound along with the rest of the list filters, so the queries stay unaware of the horizon.
    const listFilters = useMemo(() => {
        if (scheduleHorizonUpperBound) {
            const filtersWithHorizon = new URLSearchParams(filters ?? undefined)
            filtersWithHorizon.set(SCHEDULE_HORIZON_FILTER_PARAM, scheduleHorizonUpperBound)

            return filtersWithHorizon
        }

        return filters
    }, [filters, scheduleHorizonUpperBound])

    const setScheduleHorizon = (horizon: ScheduleHorizon) => {
        saveScheduleHorizon?.(horizon)
    }

    return {
        scheduleHorizon,
        setScheduleHorizon,
        isScheduleHorizonAvailable,
        scheduleHorizonUpperBound,
        listFilters,
    }
}
